'use client';

import { useTranslations, useLocale } from 'next-intl';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Link } from '@/components/ui/link';
import { MatchResultBadge, ScoreChange } from '@/components/ui/specialized';
import PlayerAvatar from '@/components/player/PlayerAvatar';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { formatDate } from '@/lib/utils';
import type { MatchWithRating } from '@/components/match/types';

interface RecentMatchesListProps {
  matches: MatchWithRating[];
}

export default function RecentMatchesList({ matches }: RecentMatchesListProps) {
  const t = useTranslations('Profile');
  const locale = useLocale();

  const recentMatches = matches.filter((m) => !m.isAdjustment).slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('recentMatches')}</CardTitle>
      </CardHeader>
      <CardContent>
        {recentMatches.length === 0 ? (
          <div className="text-muted py-6 text-center text-sm">{t('noRecentMatches')}</div>
        ) : (
          <div className="space-y-3">
            {recentMatches.map((match) => (
              <div
                key={match.id}
                className="border-border flex items-center gap-3 border-b pb-3 last:border-b-0 last:pb-0"
              >
                <MatchResultBadge result={match.result} />
                <PlayerAvatar
                  name={match.opponent}
                  profilePhoto={match.opponentProfilePhoto}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <Link
                    href={`/${locale}/user/${match.opponentUsername}`}
                    variant="subtle"
                    className="text-sm font-medium"
                    prefetch={false}
                  >
                    {match.opponent}
                  </Link>
                  {/* `dateISO` is `played_at`, a timestamptz — see ProfileHeroCard. */}
                  <LocalDateTime
                    iso={match.dateISO}
                    locale={locale}
                    format={formatDate}
                    className="text-muted block text-xs"
                  />
                </div>
                {match.myDelta !== null && (
                  <ScoreChange
                    value={Math.round(match.myDelta)}
                    isPositive={match.myDelta >= 0}
                    className="text-xs"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
